import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Modal,
  Slide,
  Stack,
  Typography,
  Box,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Checkbox,
  ListItemText,
  Menu,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as CustomRuleIcon } from "../../../../../../../vendor/_new/icon/services/owasp.svg";
import { ReactComponent as CircleaddIcon } from "../../../../../../../vendor/_new/icon/services/circle_add.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as CopyIcon } from "../../../../../../../vendor/_new/icon/sites/copy.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../../vendor/_new/icon/services/delete.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as PlusIcon } from "../../../../../../../vendor/_new/icon/sites/plus.svg";

import { GreenSelectForm, SafeTextField } from "../../../../../../common/Greens";
import { SaveButton, CancelButton } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import { RuleOperator, RuleAction, RuleTransformation, RuleKeyField } from "../../../../../../../utils/constants";
import { getRuleKeyTitle } from "../../../../../../../utils/constants";
import { ExpressionKeyField } from "../../../../../../../utils/constants";
import { getTransformationTitle, getOperatorTitle } from "../../../../../application/waf/rule/custom/component";
import colors from "../../../../../../../utils/colors";
import { UserType } from "../../../../../../../utils/constants";
import useAuth from "../../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateCustomRuleForm } from "../../../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const CustomSelectBox = ({ label = "", keys = [], getTitle = (k) => k, values = [], selectValues, padx }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // last clicked index to support shift+click range selection
  const lastClickedIndexRef = useRef(null);

  const handleMenuOpen = (target) => setAnchorEl(target);
  const handleMenuClose = () => setAnchorEl(null);

  const isSelected = (value) => !!values.find((v) => v === value);

  const setSelected = (newValues) => {
    selectValues(newValues, padx);
  };

  const handleToggleSingle = (value) => {
    if (isSelected(value)) {
      setSelected(values.filter((v) => v !== value));
    } else {
      setSelected(values.concat(value));
    }
  };
  const handleDelete = (value) => {
    selectValues(
      values.filter((item) => item !== value),
      padx
    );
  };

  // Shift+click range toggle
  const handleClickItem = (event, index, key) => {
    // if shift is pressed and there was a previous click, select range
    if (event.shiftKey && lastClickedIndexRef.current !== null) {
      const start = Math.min(lastClickedIndexRef.current, index);
      const end = Math.max(lastClickedIndexRef.current, index);

      // build new selection by toggling range ON (i.e., add all items in range)
      // You can change behavior to "match first item's state" if you prefer toggling off ranges
      const keysInRange = keys.slice(start, end + 1);
      const newSelectionSet = new Set(values);

      // we'll add all keys in range
      keysInRange.forEach((k) => newSelectionSet.add(k));

      // convert back to array preserving order of 'keys'
      const newValues = keys.filter((k) => newSelectionSet.has(k));
      setSelected(newValues);
    } else {
      // normal single toggle
      handleToggleSingle(key);
    }

    // store last clicked index (used for next shift+click)
    lastClickedIndexRef.current = index;
  };
  return (
    <>
      {/* Use your GreenSelectForm/SafeTextField if you prefer — kept simple here */}
      <Stack spacing={3}>
        <SafeTextField
          label={label}
          value={Array.isArray(values) && values.length ? `${values.length} selected` : ""}
          onClick={(e) => handleMenuOpen(e.currentTarget)}
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CircleaddIcon style={{ cursor: "pointer" }} />
              </InputAdornment>
            ),
          }}
        />
        {values?.length > 0 && (
          <Box sx={{ width: "100%", background: "white" }}>
            <Grid container spacing={1}>
              {values.map((value, index) => {
                return (
                  <Grid key={`chip_${index}`} item>
                    <Chip
                      key={value}
                      label={getTitle(value)}
                      clickable
                      deleteIcon={
                        <CloseIcon
                          onMouseDown={(event) => event.stopPropagation()}
                          style={{ width: 20, height: 20, color: colors.neutral[800] }}
                        />
                      }
                      onDelete={() => handleDelete(value)}
                      onClick={() => handleDelete(value)}
                      sx={{
                        background: `rgba(31, 193, 107, 0.1)`,
                        color: colors.green[300],
                        border: `solid 1px ${colors.green[300]}`,
                        fontSize: 14,
                        lineHeight: "25px",
                        height: 25,
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        // prevent autoFocus so keyboard doesn't steal focus (optional)
        MenuListProps={{ autoFocus: false }}
        PaperProps={{
          sx: {
            width: anchorEl ? anchorEl.clientWidth : undefined, // match width
          },
        }}
        sx={{
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        {/* the actual list - keep menu open while user selects */}
        <Box sx={{ overflow: "auto", maxHeight: 300 }}>
          {keys.map((key, index) => (
            <MenuItem
              key={key ?? `key_${index}`}
              // do NOT let MUI close the menu on click - we control open via anchor
              // we call event.stopPropagation() to avoid Select/Menu default closing behavior in some setups
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClickItem(e, index, key);
              }}
              sx={{
                ...menuItemSx,
                "& .MuiListItemText-primary": {
                  fontSize: 14,
                  lineHeight: "21px",
                  color: !!values.find((value) => value === key) ? colors.primary[300] : colors.neutral[600],
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 210,
                  "&:hover": { color: colors.primary[300] },
                },
              }}
            >
              <Checkbox
                size="small"
                checked={isSelected(key)}
                // avoid checkbox click from bubbling to menu (we handle on the MenuItem)
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClickItem(e, index, key);
                }}
                sx={{
                  color: colors.neutral[300], // border / unchecked color
                  "&.Mui-checked": {
                    color: colors.primary[300], // checkmark & filled color
                  },
                }}
              />
              <ListItemText primary={getTitle(key)} />
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </>
  );
};

const ValueComponent = ({ valueStr, selectValueStr, disabled, padx, error }) => {
  return (
    <SafeTextField
      label="Value"
      fullWidth
      value={valueStr}
      disabled={disabled}
      onChange={(e) => selectValueStr(e.target.value, padx)}
      key={`_value_${padx}`}
      error={error}
      helperText={error ? "Value is required" : ""}
    />
  );
};

const VariableComponent = ({ label, variables, selectVariables, padx }) => {
  const aKeys = [
    RuleKeyField.SOURCE_IP,
    RuleKeyField.HOST_NAME,
    RuleKeyField.USER_AGENT,
    RuleKeyField.REQUEST_HEADERS,
    RuleKeyField.REQUEST_HEADER_NAMES,
    RuleKeyField.ARGS,
    RuleKeyField.ARGS_NAMES,
    RuleKeyField.COOKIE,
    RuleKeyField.COOKIE_NAME,
    RuleKeyField.URI,
    RuleKeyField.URI_PATH,
    RuleKeyField.QUERY,
    RuleKeyField.METHOD,
    RuleKeyField.REQUEST_BODY,
    RuleKeyField.REQUEST_BODY_LENGTH,
  ];

  return CustomSelectBox({
    label,
    keys: aKeys,
    getTitle: getRuleKeyTitle,
    values: variables,
    selectValues: selectVariables,
    padx,
  });
};

const TransformationComponent = ({ label, transforms, selectTransforms, padx }) => {
  // const theme = useTheme();

  const aKeys = [
    // RuleTransformation.NONE,
    RuleTransformation.BASE64_DECODE,
    RuleTransformation.BASE64_ENCODE,
    RuleTransformation.HEX_DECODE,
    RuleTransformation.HEX_ENCODE,
    RuleTransformation.LOWERCASE,
    RuleTransformation.REMOVE_NULLS,
    RuleTransformation.REMOVE_WHITE_SPACE,
    RuleTransformation.REPLACE_NULLS,
    RuleTransformation.URL_DECODE,
    RuleTransformation.UPPERCASE,
    RuleTransformation.URL_DECODE_UNI,
    RuleTransformation.URL_ENCODE,
    RuleTransformation.UTF8_TO_UNICODE,
    RuleTransformation.TRIM_LEFT,
    RuleTransformation.TRIM_RIGHT,
    RuleTransformation.TRIM,
    RuleTransformation.DECODE_HTML_ENTITY_ESCAPE,
    RuleTransformation.DECODE_JS_ESCAPE,
    RuleTransformation.DECODE_CSS_ESCAPE,
    RuleTransformation.DECODE_CLI_ESCAPE,
    RuleTransformation.PATH_NORMALIZATION,
    RuleTransformation.COMPRESS_WHITESPACE,
    RuleTransformation.REMOVE_COMMENTS,
    RuleTransformation.LENGTH,
  ];

  return CustomSelectBox({
    label,
    keys: aKeys,
    getTitle: getTransformationTitle,
    values: transforms,
    selectValues: selectTransforms,
    padx,
  });
};

const OperatorComponent = ({ operatorStr, selectOperatorStr, disabled, padx, error }) => {
  // Order should NOT be modified
  const aConditions = [
    { operator: RuleOperator.DETECT_SQLI, negative: false },
    { operator: RuleOperator.DETECT_XSS, negative: false },
    { operator: RuleOperator.EQUALS, negative: false },
    { operator: RuleOperator.EQUALS, negative: true },
    { operator: RuleOperator.CONTAINS, negative: false },
    { operator: RuleOperator.CONTAINS, negative: true },
    { operator: RuleOperator.GREATER_THAN, negative: false },
    { operator: RuleOperator.LESS_THAN, negative: false },
    { operator: RuleOperator.GREATER_THAN_OR_EQUALS_TO, negative: false },
    { operator: RuleOperator.LESS_THAN_OR_EQUALS_TO, negative: false },
    { operator: RuleOperator.PARTIAL_MATCH, negative: false },
    { operator: RuleOperator.PARTIAL_MATCH, negative: true },
    { operator: RuleOperator.REG_EXP_MATCH, negative: false },
    { operator: RuleOperator.REG_EXP_MATCH, negative: true },
    { operator: RuleOperator.BEGINS_WITH, negative: false },
    { operator: RuleOperator.BEGINS_WITH, negative: true },
    { operator: RuleOperator.ENDS_WITH, negative: false },
    { operator: RuleOperator.ENDS_WITH, negative: true },
    { operator: RuleOperator.MATCHES_IP, negative: false },
    { operator: RuleOperator.MATCHES_IP, negative: true },
  ];
  // switch
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="operator-selector">Operator</InputLabel>
      <Select
        labelId="operator-selector"
        IconComponent={ArrowdownIcon}
        disabled={disabled}
        value={operatorStr}
        onChange={selectOperatorStr}
        sx={{ minWidth: "360px", border: "1px solid #032142CC" }}
      >
        {operatorStr === RuleOperator.NONE && (
          <MenuItem key={`_operator0_${padx}`} value={RuleOperator.NONE} disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aConditions.map((condValue, condIdx) => {
          return (
            <MenuItem key={`_operator${condIdx + 1}_${padx}`} value={(condValue.negative ? "!" : "") + condValue.operator} sx={menuItemSx}>
              {getOperatorTitle(condValue.operator, condValue.negative)}
            </MenuItem>
          );
        })}
      </Select>
    </GreenSelectForm>
  );
};

const ConfigCustomRuleSettingsModal = ({ open, handleClose, customRuleToEdit }) => {
  const { isAuthenticated, userType } = useAuth();
  const { createCustomRule, updateCustomRule, getCustomRule, setErr, errMsg, getCustomRules } = useWAFConfig();
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => setErr(null);
  }, [setErr]);

  const [ruleName, setRuleName] = useState("");
  const [conditions, setConditions] = useState([{ key: [], transform: [], operator: RuleOperator.NONE, value: "" }]);
  const [action, setAction] = useState(RuleAction.ALLOW);

  const [nameError, setNameError] = useState(false);
  const [conditionErrors, setConditionErrors] = useState([{ key: false, operator: false, value: false }]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const formValidation = useMemo(
    () => validateCustomRuleForm({ name: ruleName, conditions }),
    [ruleName, conditions],
  );

  const showNameErr = submitAttempted && formValidation.formErrors.name;

  useEffect(() => {
    if (!open) return undefined;
    window.scrollTo(0, 0);
    const init = async () => {
      if (customRuleToEdit) {
        const detailedRule = await getCustomRule(customRuleToEdit?.custom_rule_id);
        setRuleName(detailedRule?.description || undefined);
        const _conditions = detailedRule?.conditions;
        _conditions.forEach((_condition) => {
          if (!_condition.transform || !_condition.transform.length) {
            _condition.transform = [RuleTransformation.NONE];
          }
          if (_condition.negative) {
            _condition.operator = "!" + _condition.operator;
          }
        });
        setConditions(_conditions || [{ key: ["none"], transform: [RuleTransformation.NONE], operator: RuleOperator.NONE, value: "" }]);
        setConditionErrors(_conditions.map(() => ({ key: false, operator: false, value: false })));
        setAction(detailedRule?.action || RuleAction.ALLOW);
      } else {
        setRuleName("");
        setConditions([{ key: [], transform: [], operator: RuleOperator.NONE, value: "" }]);
        setConditionErrors([{ key: false, operator: false, value: false }]);
        setAction(RuleAction.ALLOW);
      }
      setNameError(false);
      setSubmitAttempted(false);
      setBaselineKey((k) => k + 1);
    };
    if (isAuthenticated) init();
    return () => setErr(null);
  }, [open, customRuleToEdit, getCustomRule, isAuthenticated, setErr]);

  const ruleNameChange = (event) => {
    if (userType === UserType.ORG_USER) return;
    setRuleName(event.target.value);
    if (event.target.value.trim() !== "") setNameError(false);
  };

  const selectVariables = (keys, i) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    if (i < list.length) {
      list[i]["key"] = keys;
    }
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].key = false;
      setConditionErrors(errList);
    }
  };
  const selectTransforms = (transforms, i) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    if (i < list.length) {
      list[i]["transform"] = transforms;
    }
    setConditions(list);
  };
  const selectOperatorStr = (event, i) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    if (i < list.length) {
      list[i]["operator"] = event.target.value;
    }
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].operator = false;
      setConditionErrors(errList);
    }
  };
  const selectValueStr = (value, i) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    if (i < list.length) {
      list[i]["value"] = value;
    }
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].value = false;
      setConditionErrors(errList);
    }
  };
  const duplicateClick = (i) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    if (i < list.length) {
      list.push(JSON.parse(JSON.stringify(list[i])));
    }
    setConditions(list);
    setConditionErrors([...conditionErrors, { key: false, operator: false, value: false }]);
  };

  const addClick = (e) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list.push({ key: [], transform: [], operator: RuleOperator.NONE, value: "" });
    setConditions(list);
    setConditionErrors([...conditionErrors, { key: false, operator: false, value: false }]);
  };

  const removeClick = (i) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    if (i < list.length) {
      list.splice(i, 1);
    }
    if (0 === list.length) {
      list.push({ key: [], transform: [], operator: RuleOperator.NONE, value: "" });
    }
    setConditions(list);
    let errList = [...conditionErrors];
    if (i < errList.length) {
      errList.splice(i, 1);
    }
    if (0 === errList.length) {
      errList.push({ key: false, operator: false, value: false });
    }
    setConditionErrors(errList);
  };

  const [loading, setLoading] = useState(false);

  const formSnapshot = useMemo(
    () => ({ ruleName, conditions, action }),
    [ruleName, conditions, action],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, { disabled: loading });

  const handleSave = async () => {
    if (userType === UserType.ORG_USER) return;
    setSubmitAttempted(true);
    const validation = validateCustomRuleForm({ name: ruleName, conditions });
    if (!validation.valid) {
      setConditionErrors(validation.rowErrors.map((e) => ({
        key: e.key || false,
        operator: e.operator || false,
        value: e.value || false,
      })));
      return;
    }
    const _conditions = JSON.parse(JSON.stringify(conditions)); // Deep copy of conditions
    _conditions.forEach((andCond) => {
      if (-1 < andCond.operator.indexOf("!")) {
        andCond.operator = andCond.operator.substring(1);
        andCond.negative = true;
      } else {
        andCond.negative = false;
      }
      if (1 === andCond.transform.length && RuleTransformation.NONE === andCond.transform[0]) {
        andCond.transform = [];
      }
      if (RuleOperator.DETECT_SQLI === andCond.operator || RuleOperator.DETECT_XSS === andCond.operator) {
        andCond.value = "";
      }
    });
    setLoading(true);
    const result = customRuleToEdit
      ? await updateCustomRule(customRuleToEdit?.custom_rule_id, { description: ruleName, conditions: _conditions, action: action })
      : await createCustomRule({ description: ruleName, conditions: _conditions, action: action });
    if ("success" === result.status) {
      await getCustomRules();
      showSuccess(result.msg);
      handleClose();
    } else {
      showError(result.msg);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  return (
    <>
      <Modal 
        open={open} 
        onClose={requestClose}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
          }
        }}
      >
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CustomRuleIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                    <Typography
                      sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                    >
                      {customRuleToEdit ? "Edit custom rule" : "Add new custom rule"}
                    </Typography>
                    <FormHelpTooltip tooltip="Build a ModSecurity-style rule with match conditions and an action (block, challenge, log). Custom rules run after built-in WAF engines on matching requests." />
                  </Stack>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Set conditions to customize your security rules.
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
                <CloseIcon sx={{ color: colors.neutral[600] }} />
              </IconButton>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
              <ReactPerfectScrollbar>
                <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                  <Stack spacing={4}>
                    <SafeTextField label={"Rule name"} value={ruleName} onChange={ruleNameChange} fullWidth error={Boolean(showNameErr)} helperText={showNameErr || ""} />
                    <Stack direction="column" spacing={6}>
                      {conditions.map((andCond, i) => {
                        return (
                          <Box
                            key={`cond_${i}`}
                            sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}
                          >
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                                  {i === 0 ? "When incoming requests match..." : "And"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <VariableComponent label="Fields" variables={andCond.key} selectVariables={selectVariables} padx={i} />
                              </Grid>
                              <Grid item xs={12}>
                                <TransformationComponent
                                  label="Transformation"
                                  transforms={andCond.transform}
                                  selectTransforms={selectTransforms}
                                  disabled={andCond.key?.length === 0}
                                  padx={i}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <OperatorComponent
                                  operatorStr={andCond.operator}
                                  selectOperatorStr={(event) => selectOperatorStr(event, i)}
                                  disabled={andCond.key?.length === 0}
                                  padx={i}
                                  error={submitAttempted && formValidation.rowErrors[i]?.operator}
                                />
                              </Grid>
                              {!(andCond.operator === RuleOperator.DETECT_SQLI || andCond.operator === RuleOperator.DETECT_XSS) && (
                                <Grid item xs={12}>
                                  <ValueComponent
                                    valueStr={andCond.value}
                                    selectValueStr={(event) => selectValueStr(event, i)}
                                    disabled={andCond.key === ExpressionKeyField.NONE || andCond.operator === "none"}
                                    padx={i}
                                    error={submitAttempted && formValidation.rowErrors[i]?.value}
                                  />
                                </Grid>
                              )}
                              <Grid item xs={12}>
                                <Stack width={"100%"} spacing={2} direction="row" justifyContent="end" alignItems="center">
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    spacing={1}
                                    onClick={addClick}
                                    sx={{
                                      height: 32,
                                      cursor: "pointer",
                                      borderRadius: "8px",
                                      border: `solid 1px ${colors.secondary[300]}`,
                                      px: 2,
                                      py: 1,
                                    }}
                                  >
                                    <PlusIcon style={{ color: colors.neutral[800] }} />
                                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                                      Add new
                                    </Typography>
                                  </Stack>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    spacing={1}
                                    onClick={() => duplicateClick(i)}
                                    sx={{
                                      height: 32,
                                      cursor: "pointer",
                                      borderRadius: "8px",
                                      border: `solid 1px ${colors.secondary[300]}`,
                                      px: 2,
                                      py: 1,
                                    }}
                                  >
                                    <CopyIcon style={{ color: colors.neutral[800] }} />
                                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                                      Duplicate
                                    </Typography>
                                  </Stack>
                                  <Box onClick={() => removeClick(i)} sx={{ cursor: "pointer" }}>
                                    <DeleteIcon style={{ color: colors.neutral[600] }} />
                                  </Box>
                                </Stack>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                    </Stack>
                    <GreenSelectForm fullWidth>
                      <InputLabel id="then">Then</InputLabel>
                      <Select
                        labelId="then"
                        IconComponent={ArrowdownIcon}
                        value={action}
                        onChange={(event) => setAction(event.target.value)}
                      >
                        <MenuItem key={`action_${RuleAction.ALLOW}`} value={RuleAction.ALLOW} sx={menuItemSx}>
                          Allow
                        </MenuItem>
                        <MenuItem key={`action_${RuleAction.BLOCK}`} value={RuleAction.BLOCK} sx={menuItemSx}>
                          Block
                        </MenuItem>
                        <MenuItem key={`action_${RuleAction.LOG}`} value={RuleAction.LOG} sx={menuItemSx}>
                          Log
                        </MenuItem>
                      </Select>
                    </GreenSelectForm>
                    <Stack
                      direction={"row"}
                      spacing={2}
                      sx={{
                        minHeight: 48,
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: colors.primary["alpha"],
                        border: `solid 1px ${colors.primary[300]}`,
                      }}
                    >
                      <Box sx={{ width: 24, height: 24 }}>
                        <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                      </Box>
                      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                        You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} spacing={3}>
                    <SaveButton onClick={handleSave} disabled={loading} />
                    <CancelButton onClick={requestClose} />
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>
      {confirmDialog}
    </>
  );
};
export default ConfigCustomRuleSettingsModal;
